export enum ChainType {
  sol = "sol",
  evm = "evm",
  hyperliquid = "hyperliquid",
}

export enum Chain {
  ethereum = "ethereum",
  solana = "solana",
  hyperliquid = "hyperliquid",
  sei = "sei",
  monaco_spot = "monaco_spot",
  monaco_perps = "monaco_perps",
}

export const resolveChain = (chainType: ChainType) => {
  switch (chainType) {
    case ChainType.sol:
      return Chain.solana;
    case ChainType.evm:
      return Chain.sei;
    case ChainType.hyperliquid:
      return Chain.hyperliquid;
    default:
      throw new Error(`Invalid chain type: ${chainType}`);
  }
};

export enum Protocol {
  PROTOCOL_UNSPECIFIED = "PROTOCOL_UNSPECIFIED",
  UNISWAP_V3 = "UNISWAP_V3",
  UNISWAP_V2 = "UNISWAP_V2",
  UNISWAP_V1 = "UNISWAP_V1",
  RAYDIUM = "RAYDIUM",
  GOOSEFX_SSL = "GOOSEFX_SSL",
  METEORA = "METEORA",
  LIFINITY = "LIFINITY",
  PHOENIX = "PHOENIX",
  WHIRLPOOL = "WHIRLPOOL",
  PUMP = "PUMP",
  MOONSHOT = "MOONSHOT",
  EGG = "EGG",
  RAYDIUM_CPMM = "RAYDIUM_CPMM",
  RAYDIUM_CLMM = "RAYDIUM_CLMM",
  ORCA_WHIRLPOOL = "ORCA_WHIRLPOOL",
  METEORA_DLMM = "METEORA_DLMM",
  METEORA_DYN = "METEORA_DYN",
  SUNPUMP = "SUNPUMP",
  GRAFUN = "GRAFUN",
  PUMPSWAP = "PUMPSWAP",
  METEORA_DYN_V2 = "METEORA_DYN_V2",
  METEORA_VIRTUAL_CURVE = "METEORA_VIRTUAL_CURVE",
  METEORA_VIRTUAL_CURVE_BELIEVE = "METEORA_VIRTUAL_CURVE_BELIEVE",
  RAYDIUM_LAUNCHPAD = "RAYDIUM_LAUNCHPAD",
  RAYDIUM_LAUNCHPAD_BONK = "RAYDIUM_LAUNCHPAD_BONK",
  BOOP = "BOOP",
  METEORA_VIRTUAL_CURVE_JUPITER_STUDIO = "METEORA_VIRTUAL_CURVE_JUPITER_STUDIO",
  METEORA_VIRTUAL_CURVE_MOONSHOT = "METEORA_VIRTUAL_CURVE_MOONSHOT",
  METEORA_VIRTUAL_CURVE_BAGS = "METEORA_VIRTUAL_CURVE_BAGS",
  METEORA_VIRTUAL_CURVE_COOKING_CITY = "METEORA_VIRTUAL_CURVE_COOKING_CITY",
  SAILOR = "SAILOR",
  DRAGONSWAP_V1 = "DRAGONSWAP_V1",
  DRAGONSWAP_V2 = "DRAGONSWAP_V2",
  HYPERLIQUID = "HYPERLIQUID",
  MONACO = "MONACO",
}

export const getProtocolName = (protocol: Protocol) => {
  switch (protocol) {
    case Protocol.UNISWAP_V3:
      return "Uniswap V3";
    case Protocol.UNISWAP_V2:
      return "Uniswap V2";
    case Protocol.UNISWAP_V1:
      return "Uniswap V1";
    case Protocol.RAYDIUM:
      return "Raydium";
    case Protocol.GOOSEFX_SSL:
      return "GooseFX SSL";
    case Protocol.METEORA:
      return "Meteora";
    case Protocol.LIFINITY:
      return "Lifinity";
    case Protocol.PHOENIX:
      return "Phoenix";
    case Protocol.WHIRLPOOL:
      return "Whirlpool";
    case Protocol.PUMP:
      return "Pump";
    case Protocol.MOONSHOT:
      return "Moonit";
    case Protocol.EGG:
      return "Egg";
    case Protocol.RAYDIUM_CPMM:
      return "Raydium CPMM";
    case Protocol.RAYDIUM_CLMM:
      return "Raydium CLMM";
    case Protocol.ORCA_WHIRLPOOL:
      return "Orca Whirlpool";
    case Protocol.METEORA_DLMM:
      return "Meteora DLMM";
    case Protocol.METEORA_DYN:
      return "Meteora DYN";
    case Protocol.SUNPUMP:
      return "Sunpump";
    case Protocol.GRAFUN:
      return "Grafun";
    case Protocol.PUMPSWAP:
      return "Pumpswap";
    case Protocol.METEORA_DYN_V2:
      return "Meteora DYN V2";
    case Protocol.METEORA_VIRTUAL_CURVE:
      return "Dynamic BC";
    case Protocol.METEORA_VIRTUAL_CURVE_BELIEVE:
      return "Believe";
    case Protocol.RAYDIUM_LAUNCHPAD:
      return "Raydium Pad";
    case Protocol.RAYDIUM_LAUNCHPAD_BONK:
      return "Bonk";
    case Protocol.BOOP:
      return "Boop";
    case Protocol.METEORA_VIRTUAL_CURVE_JUPITER_STUDIO:
      return "Jupiter Studio";
    case Protocol.METEORA_VIRTUAL_CURVE_MOONSHOT:
      return "Moonshot";
    case Protocol.METEORA_VIRTUAL_CURVE_BAGS:
      return "Bags";
    case Protocol.METEORA_VIRTUAL_CURVE_COOKING_CITY:
      return "Cooking City";
    case Protocol.HYPERLIQUID:
      return "Hyperliquid";
    case Protocol.MONACO:
      return "Monaco";
    default:
      return protocol;
  }
};

export const isTrenchProtocol = (
  protocol: Protocol | string | null | undefined,
): boolean => {
  const trenchProtocols = new Set([
    Protocol.PUMP,
    Protocol.MOONSHOT,
    Protocol.METEORA_VIRTUAL_CURVE,
    Protocol.METEORA_VIRTUAL_CURVE_BELIEVE,
    Protocol.METEORA_VIRTUAL_CURVE_JUPITER_STUDIO,
    Protocol.METEORA_VIRTUAL_CURVE_MOONSHOT,
    Protocol.METEORA_VIRTUAL_CURVE_BAGS,
    Protocol.METEORA_VIRTUAL_CURVE_COOKING_CITY,
    Protocol.RAYDIUM_LAUNCHPAD,
    Protocol.RAYDIUM_LAUNCHPAD_BONK,
    Protocol.BOOP,
  ]);
  return protocol != null && trenchProtocols.has(protocol as Protocol);
};

export const isOrderingUnsupported = (
  protocol: Protocol,
  inverted: boolean,
) => {
  return protocol === Protocol.PUMPSWAP && inverted;
};

export const isOrderbookSupportedForChain = (chain: Chain) => {
  return (
    chain === Chain.hyperliquid ||
    chain === Chain.monaco_spot ||
    chain === Chain.monaco_perps
  );
};
export type IndexedChain = Chain.solana | Chain.sei;

export const isChainIndexed = (chain: Chain): chain is IndexedChain => {
  return [Chain.solana, Chain.sei].includes(chain as IndexedChain);
};

const SPOT_CHAINS = [
  Chain.ethereum,
  Chain.solana,
  Chain.sei,
  Chain.monaco_spot,
] as const;
export type SpotChain = (typeof SPOT_CHAINS)[number];

export const isChainSpot = (chain: Chain): chain is SpotChain => {
  return SPOT_CHAINS.includes(chain as SpotChain);
};

export type MonacoChain = Chain.monaco_spot | Chain.monaco_perps;

export const isMonacoChain = (chain: Chain): chain is MonacoChain => {
  return chain === Chain.monaco_spot || chain === Chain.monaco_perps;
};

/**
 * Chains the backend's wallets API understands. Monaco spot and perps
 * share the same underlying Sei wallet, so the backend only stores
 * `monaco_spot` — the `monaco_perps` value exists purely as a frontend
 * routing/UI flag and must be folded back to `monaco_spot` before any
 * wallet read/write hits the API. Without this normalization the
 * backend rejects creates with "chain must be one of the following
 * values: …" and all chain-filtered reads (balances, withdraw wallet
 * picker, etc.) return an empty list on Monaco Perps pages.
 */
export type WalletChain =
  | Chain.solana
  | Chain.ethereum
  | Chain.sei
  | Chain.hyperliquid
  | Chain.monaco_spot;

export const toWalletChain = (chain: Chain): WalletChain => {
  if (chain === Chain.monaco_perps) return Chain.monaco_spot;
  return chain as WalletChain;
};
